import { resetAssetCache } from "../lib/assets.ts";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { VaultAction, VaultData, VaultEvent, VaultItem } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Pager } from "../components/Pager.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, ChainLogo, CopyButton, EmptyState, ExternalLink, Hash, PageHeader, Spinner, StatusPill, Time } from "../components/ui.tsx";
import { fmt, plural, useT } from "../i18n/index.tsx";
import { chainName, formatTimeFull, exactUnits, formatUnits, formatUtc, parseUnits, timeAgo, tokenOf } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { useClientPages } from "../lib/paging.ts";
import { useSession } from "../lib/session.tsx";
import { VaultActionDialog } from "./VaultActions.tsx";
import { Networks, vaultAnchor } from "./VaultNetworks.tsx";

/** "4 hours" / "30 minutes" from seconds. */
function delayText(seconds: number, t: ReturnType<typeof useT>) {
  if (seconds >= 3600 && seconds % 3600 === 0) return plural(seconds / 3600, t.vault.hoursN);
  if (seconds >= 3600) return plural(Math.round(seconds / 360) / 10, t.vault.hoursN);
  return plural(Math.max(1, Math.round(seconds / 60)), t.vault.minutesN);
}

export function Vault() {
  const t = useT();
  const q = useAsync(() => api.get<VaultData>("/vault"), []);
  const items = q.data?.items ?? [];
  const shared = items.some((v) => v.shared);

  // On-chain state refreshes about once a minute: follow it.
  const setData = q.setData;
  useEffect(() => {
    const timer = setInterval(() => {
      api.get<VaultData>("/vault").then(setData).catch(() => {});
    }, 30_000);
    return () => clearInterval(timer);
  }, [setData]);

  return (
    <div className="page">
      <PageHeader title={t.vault.title}>{shared ? t.vault.shared.intro : t.vault.intro}</PageHeader>
      {q.error ? (
        <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void q.reload()}>{t.common.retry}</button>}>{errorMessage(q.error)}</Alert>
      ) : q.loading && !q.data ? (
        <div className="card"><span className="sk sk-lg" /><span className="sk" /><span className="sk" /></div>
      ) : !items.length ? (
        <>
          {/* No vault yet: lead straight to the networks the brand can enable itself. */}
          <EmptyState title={t.vault.noVaultsTitle}>{t.vault.networks.emptyLead}</EmptyState>
          <Networks vaults={items} onActivated={() => { resetAssetCache(); void q.reload(); }} />
        </>
      ) : (
        <>
          {items.map((v) => v.shared ? <SharedVaultSection key={v.asset} v={v} /> : <VaultSection key={v.asset} v={v} onChanged={() => void q.reload()} />)}
          <Networks vaults={items} onActivated={() => { resetAssetCache(); void q.reload(); }} />
          {/* A shared vault's movements belong to every sandbox account: only list the brand's own vaults. */}
          <Movements events={q.data?.events ?? []} items={items.filter((v) => !v.shared)} />
        </>
      )}
    </div>
  );
}

function Figure({ label, value, token, hint }: { label: string; value: string | null; token: string; hint?: string }) {
  return (
    <div className="vfig">
      <div className="vfig-label">{label}</div>
      <div className="vfig-value">{value ?? "–"} {value !== null && <span>{token}</span>}</div>
      {hint && <div className="vfig-hint">{hint}</div>}
    </div>
  );
}

/** Sandbox sign-up: the shared test vault. Only this brand's ledger figures; no on-chain balance, limits or owner actions. */
function SharedVaultSection({ v }: { v: VaultItem }) {
  const t = useT();
  const u = (x: string | null | undefined) => (x === null || x === undefined ? null : formatUnits(x, v.decimals));
  const label = fmt(t.vault.sectionLabel, { token: v.token, chain: chainName(v.chain) });
  return (
    <section className="card vault" aria-label={label} id={vaultAnchor(v.asset)}>
      <header className="vault-head">
        <div className="vault-title">
          <ChainLogo chain={v.chain} size={26} />
          <h2>{label}</h2>
          <span className="pill no-dot pill-grey">{t.vault.shared.pill}</span>
        </div>
        <dl className="vault-ids">
          <div><dt>{t.vault.vaultAddress}</dt><dd><Hash value={v.vault_address} href={v.vault_url} />{v.vault_url && <ExternalLink href={v.vault_url}>{t.vault.viewOnExplorer}</ExternalLink>}</dd></div>
        </dl>
      </header>
      <div className="vault-figures">
        <Figure label={t.vault.availableInLedger} value={u(v.available)} token={v.token} />
        <Figure label={t.vault.pendingPayouts} value={u(v.pending_payouts)} token={v.token} />
        {v.pending_sweep !== undefined && <Figure label={t.vault.pendingSweep} value={u(v.pending_sweep)} token={v.token} />}
      </div>
      <div className="vault-block"><p className="muted small">{t.vault.shared.note}</p></div>
    </section>
  );
}

function VaultSection({ v, onChanged }: { v: VaultItem; onChanged: () => void }) {
  const t = useT();
  const { can } = useSession();
  const [action, setAction] = useState<VaultAction | null>(null);
  const [topUp, setTopUp] = useState(false);
  const u = (x: string | null | undefined) => (x === null || x === undefined ? null : formatUnits(x, v.decimals));
  const big = (x: string | null | undefined) => (x === null || x === undefined ? null : BigInt(x));
  const limitsOff = v.per_tx_limit === "0";
  const daily = big(v.daily_limit);
  const spent = BigInt(v.spent_today || "0");
  const pct = daily && daily > 0n ? Math.min(100, Number((spent * 1000n) / daily) / 10) : 0;
  const floatShort = big(v.float) !== null && big(v.float)! < BigInt(v.pending_payouts || "0");
  const lowToday = !limitsOff && daily !== null && daily > 0n && v.remaining_today !== null && BigInt(v.remaining_today) * 10n < daily;
  const sweep = big(v.pending_sweep) ?? 0n;
  const label = fmt(t.vault.sectionLabel, { token: v.token, chain: chainName(v.chain) });

  return (
    <section className="card vault" aria-label={label} id={vaultAnchor(v.asset)}>
      <header className="vault-head">
        <div className="vault-title">
          <ChainLogo chain={v.chain} size={26} />
          <h2>{label}</h2>
          {v.paused !== null && <StatusPill status={v.paused ? "failed" : "active"} label={v.paused ? t.vault.statusPaused : t.vault.statusActive} />}
        </div>
        <dl className="vault-ids">
          <div><dt>{t.vault.vaultAddress}</dt><dd><Hash value={v.vault_address} href={v.vault_url} />{v.vault_url && <ExternalLink href={v.vault_url}>{t.vault.viewOnExplorer}</ExternalLink>}</dd></div>
          <div><dt>{t.vault.ownerAddress}</dt><dd>{v.owner ? <Hash value={v.owner} /> : <span className="muted">{t.vault.unknown}</span>}</dd></div>
        </dl>
        <div className="vault-head-actions">
          <p className="vault-checked muted small" title={formatUtc(v.checked_at)}>{v.checked_at ? fmt(t.vault.checkedAgo, { t: timeAgo(v.checked_at) }) : t.vault.notChecked}</p>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setTopUp(true)}>{t.vault.topUp}</button>
        </div>
      </header>
      {topUp && <TopUpDialog v={v} onClose={() => setTopUp(false)} />}

      {v.error && <div className="vault-block"><Alert tone="error">{fmt(t.vault.readError, { error: v.error })}</Alert></div>}

      <div className="vault-figures">
        <Figure label={t.vault.balanceInVault} value={u(v.balance)} token={v.token} hint={t.vault.inVaultHint} />
        <Figure label={t.vault.float} value={u(v.float)} token={v.token} hint={t.vault.floatHint} />
        <Figure label={t.vault.availableInLedger} value={u(v.available)} token={v.token} />
        <Figure label={t.vault.pendingPayouts} value={u(v.pending_payouts)} token={v.token} />
      </div>

      {v.pending_sweep !== undefined && (
        <div className="vault-block vault-sweep">
          <div className="vault-split">
            <div><span className="muted">{t.vault.inVault}</span> <strong className="num">{u(v.balance) ?? "–"} {v.token}</strong></div>
            <div><span className="muted">{t.vault.pendingSweep}</span> <strong className="num">{u(v.pending_sweep)} {v.token}</strong></div>
          </div>
          <p className="muted small">
            {t.vault.bothCounted}{" "}
            {sweep > 0n && v.sweep_rule && (
              <>
                {v.sweep_rule.threshold === "0"
                  ? t.vault.sweptSeconds
                  : fmt(t.vault.sweptWhen, { threshold: `${u(v.sweep_rule.threshold)} ${v.token}`, delay: delayText(v.sweep_rule.max_delay_seconds, t) })}{" "}
                {v.expected_sweep_by && <span title={formatUtc(v.expected_sweep_by)}>{fmt(t.vault.latestBy, { time: formatTimeFull(v.expected_sweep_by) })}</span>}
              </>
            )}
            {(v.waiting_for_sweep ?? 0) > 0 && (
              <> <Link to="/payouts?status=queued">{plural(v.waiting_for_sweep ?? 0, t.vault.waitingForSweep)}</Link></>
            )}
          </p>
        </div>
      )}

      <div className="vault-block">
        <h3 className="vault-sub">{t.vault.limits}</h3>
        {limitsOff ? (
          <div className="limits-off">
            <strong>{t.vault.limitsOffTitle}</strong>
            <p>{t.vault.limitsOffBody}</p>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAction("setLimits")}>{t.vault.actions.setLimits}</button>
          </div>
        ) : (
          <>
            <div className="limit-figs">
              <div><span className="muted">{t.vault.perPayoutLimit}</span> <strong className="num">{v.per_tx_limit === null ? t.vault.noLimit : `${u(v.per_tx_limit)} ${v.token}`}</strong></div>
              <div><span className="muted">{t.vault.dailyLimit}</span> <strong className="num">{v.daily_limit === null ? t.vault.noLimit : `${u(v.daily_limit)} ${v.token}`}</strong></div>
            </div>
            {daily !== null && daily > 0n ? (
              <div className="usage">
                <div className={`usage-bar ${pct >= 100 ? "full" : pct >= 80 ? "high" : ""}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}>
                  <span style={{ inlineSize: `${pct}%` }} />
                </div>
                <p className="usage-text">
                  {fmt(t.vault.usedToday, { used: u(v.spent_today) ?? "0", limit: u(v.daily_limit) ?? "", token: v.token, left: u(v.remaining_today) ?? "0" })}
                  <span className="muted"> {t.vault.limitsCountFee}</span>
                </p>
              </div>
            ) : (
              <p className="usage-text muted">{fmt(t.vault.usedTodayNoLimit, { used: u(v.spent_today) ?? "0", token: v.token })}</p>
            )}
          </>
        )}
      </div>

      {(v.paused || v.operator_ok === false || floatShort || v.waiting_payouts > 0 || lowToday || v.low_balance) && (
        <div className="vault-block vault-warnings">
          {v.low_balance && (
            <Alert tone="warn" action={<button type="button" className="btn btn-ghost btn-sm" onClick={() => setTopUp(true)}>{t.vault.topUp}</button>}>
              {t.vault.lowBalance}
            </Alert>
          )}
          {v.paused && <Alert tone="error">{t.vault.warnPaused}</Alert>}
          {v.operator_ok === false && <Alert tone="error">{t.vault.warnOperator}</Alert>}
          {floatShort && <Alert tone="warn">{fmt(t.vault.warnFloat, { float: `${u(v.float)} ${v.token}`, pending: `${u(v.pending_payouts)} ${v.token}` })}</Alert>}
          {lowToday && <Alert tone="warn">{fmt(t.vault.warnLowToday, { left: u(v.remaining_today) ?? "0", token: v.token })}</Alert>}
          {v.waiting_payouts > 0 && (
            <Alert tone="warn" action={
              <>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTopUp(true)}>{t.vault.topUp}</button>
                <Link className="btn btn-ghost btn-sm" to="/payouts?status=queued">{t.vault.seeWaiting}</Link>
              </>
            }>
              {plural(v.waiting_payouts, t.vault.warnWaiting)}
            </Alert>
          )}
        </div>
      )}

      <div className="vault-block vault-cold">
        <div className="cold-wallet">
          <h3 className="vault-sub">{t.vault.coldWallet}</h3>
          {v.cold_wallet ? <Hash value={v.cold_wallet} full /> : <span className="muted">{t.vault.notSet}</span>}
          <p className="muted small">{t.vault.coldWalletHint}</p>
        </div>
        <AutoSweep v={v} editable={can("vault:edit")} onSaved={onChanged} />
        {v.platform_cap && <PlatformCap v={v} />}
      </div>

      <div className="vault-block vault-actions">
        <h3 className="vault-sub">{t.vault.ownerActions}</h3>
        <p className="muted small">{t.vault.ownerActionsHint}</p>
        <div className="btn-row">
          {(["setLimits", "setFloat", "setColdWallet", "withdraw"] as const).map((a) => (
            <button key={a} type="button" className="btn btn-ghost btn-sm" onClick={() => setAction(a)}>{t.vault.actions[a]}</button>
          ))}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAction("sweepToCold")} disabled={!v.cold_wallet} title={!v.cold_wallet ? t.vault.sweepNeedsCold : undefined}>
            {t.vault.actions.sweepToCold}
          </button>
          {v.paused
            ? <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAction("unpause")}>{t.vault.actions.unpause}</button>
            : <button type="button" className="btn btn-ghost btn-sm btn-danger-ghost" onClick={() => setAction("pause")}>{t.vault.actions.pause}</button>}
        </div>
      </div>

      {action && <VaultActionDialog vault={v} action={action} onClose={() => setAction(null)} onSent={onChanged} />}
    </section>
  );
}

/** Where to send funds for payouts: the vault address (QR + copy). A top-up is credited without a fee (vault.topup). */
function TopUpDialog({ v, onClose }: { v: VaultItem; onClose: () => void }) {
  const t = useT();
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import("qrcode")
      .then(({ default: QRCode }) => QRCode.toDataURL(v.vault_address, { margin: 1, width: 320, color: { dark: "#0b1e46", light: "#ffffff" } }))
      .then((u) => alive && setQr(u))
      .catch(() => alive && setQr(null));
    return () => {
      alive = false;
    };
  }, [v.vault_address]);
  return (
    <Dialog open onClose={onClose} title={fmt(t.vault.topUpTitle, { token: v.token, chain: chainName(v.chain) })} description={fmt(t.vault.topUpIntro, { token: v.token })}>
      <div className="form-stack">
        <div className="pay-address">
          <div className="qr">{qr ? <img src={qr} alt={t.vault.networks.qrAlt} width={160} height={160} /> : <Spinner />}</div>
          <div className="pay-address-text">
            <span className="field-label">{chainName(v.chain)} · {v.token}</span>
            <div className="owner-address"><code>{v.vault_address}</code><CopyButton value={v.vault_address} label={t.common.copy} /></div>
            <p className="pay-warning">{fmt(t.vault.networks.sendOnly, { tokens: v.token, chain: chainName(v.chain) })}</p>
          </div>
        </div>
        {v.platform_cap && <p className="muted small">{fmt(t.vault.topUpCap, { cap: formatUnits(v.platform_cap.max, v.decimals), token: v.token })}</p>}
        <div className="dlg-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>{t.common.done}</button>
        </div>
      </div>
    </Dialog>
  );
}

/** iCryptoNow's daily cap: what the vault holds above it goes to the cold wallet registered on the vault. */
function PlatformCap({ v }: { v: VaultItem }) {
  const t = useT();
  const cap = v.platform_cap!;
  const last = cap.last;
  return (
    <div className="auto-sweep">
      <h3 className="vault-sub">{t.vault.capTitle}</h3>
      <p className="muted small">{fmt(t.vault.capHint, { cap: formatUnits(cap.max, v.decimals), token: v.token })}</p>
      {last?.status === "sent" && last.amount && (
        <p className="small" title={formatUtc(last.at)}>
          {fmt(t.vault.capLast, { amount: formatUnits(last.amount, v.decimals), token: v.token, time: last.at ? timeAgo(last.at) : "" })}
        </p>
      )}
      {last?.status === "refused" && <p className="sweep-note">{fmt(t.vault.capRefused, { reason: last.reason ?? "" })}</p>}
    </div>
  );
}

function AutoSweep({ v, editable, onSaved }: { v: VaultItem; editable: boolean; onSaved: () => void }) {
  const t = useT();
  const toast = useToast();
  const ac = v.auto_cold;
  const [mode, setMode] = useState(ac.mode);
  const [hour, setHour] = useState(ac.hour_utc);
  const [threshold, setThreshold] = useState(ac.threshold ? formatUnits(ac.threshold, v.decimals, 2, v.decimals).replace(/,/g, "") : "");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const raw = mode === "above" ? parseUnits(threshold, v.decimals) : null;
  const dirty = mode !== ac.mode || (mode === "daily" && hour !== ac.hour_utc) || (mode === "above" && raw !== ac.threshold);

  const summary =
    ac.mode === "off" ? t.vault.modeOff
      : ac.mode === "daily" ? fmt(t.vault.dailyAt, { hour: String(ac.hour_utc).padStart(2, "0") })
        : fmt(t.vault.aboveSummary, { amount: formatUnits(ac.threshold ?? "0", v.decimals), token: v.token });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === "above" && (!raw || raw === "0")) return setError(t.vault.thresholdInvalid);
    if (code.length !== 6) return setCodeError(t.common.errors.mfa_required);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      const body = mode === "above" ? { mode, threshold: raw, code } : mode === "daily" ? { mode, hour_utc: hour, code } : { mode, code };
      await api.put(`/vault/${encodeURIComponent(v.asset)}/auto-cold`, body);
      setCode("");
      toast(t.vault.sweepSaved);
      onSaved();
    } catch (err) {
      setCode("");
      if (isCode(err, "mfa_required")) setCodeError(t.common.errors.mfa_required);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auto-sweep">
      <h3 className="vault-sub">{t.vault.autoSweep}</h3>
      <p className="muted small">{t.vault.autoSweepHint}</p>
      {ac.note && <p className="sweep-note">{fmt(t.vault.skippedNote, { note: ac.note })}</p>}
      <p className="muted small" title={formatUtc(ac.last_at)}>{ac.last_at ? fmt(t.vault.lastRun, { time: timeAgo(ac.last_at) }) : t.vault.neverRun}</p>
      {!editable ? (
        <p><strong>{summary}</strong> <span className="muted small">· {t.vault.readOnlySweep}</span></p>
      ) : (
        <form className="form-stack sweep-form" onSubmit={save} noValidate>
          <div className="seg" role="radiogroup" aria-label={t.vault.autoSweep}>
            {(["off", "daily", "above"] as const).map((m) => (
              <button key={m} type="button" role="radio" aria-checked={mode === m} aria-pressed={mode === m} onClick={() => setMode(m)}>
                {m === "off" ? t.vault.modeOff : m === "daily" ? t.vault.modeDaily : t.vault.modeAbove}
              </button>
            ))}
          </div>
          {mode !== "off" && !v.cold_wallet && <p className="field-hint">{t.vault.noColdWalletForSweep}</p>}
          {mode === "daily" && (
            <div className="field sweep-field">
              <label htmlFor={`hour-${v.asset}`}>{t.vault.hourLabel}</label>
              <div className="select-wrap">
                <select id={`hour-${v.asset}`} value={hour} onChange={(e) => setHour(Number(e.target.value))}>
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>)}
                </select>
              </div>
            </div>
          )}
          {mode === "above" && (
            <div className="field sweep-field">
              <label htmlFor={`thr-${v.asset}`}>{t.vault.aboveLabel}</label>
              <div className="input-affix">
                <input id={`thr-${v.asset}`} inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} placeholder="50000" />
                <span className="affix">{v.token}</span>
              </div>
            </div>
          )}
          {dirty && <CodeInput value={code} onChange={(c) => { setCode(c); setCodeError(null); }} error={codeError} disabled={busy} />}
          {error && <Alert tone="error">{error}</Alert>}
          <div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !dirty}>{busy && <Spinner />} {t.vault.saveSweep}</button>
          </div>
        </form>
      )}
    </div>
  );
}

function Movements({ events, items }: { events: VaultEvent[]; items: VaultItem[] }) {
  const t = useT();
  // Only movements of this brand's vault assets (their decimals are known).
  const known = events.filter((e) => items.some((i) => i.asset === e.asset));
  const pg = useClientPages(known, "mv_", 25);
  const dec = (asset: string) => items.find((i) => i.asset === asset)?.decimals ?? 6;
  const status = (s: string) => (t.vault.movementStatus as Record<string, string>)[s] ?? s;
  const cols: Column<VaultEvent>[] = [
    { key: "time", header: t.vault.colTime, cell: (e) => <Time iso={e.created_at} />, mobile: "title" },
    { key: "dir", header: t.vault.colDirection, cell: (e) => <span className={`dir dir-${e.direction}`}>{e.direction === "in" ? t.vault.dirIn : t.vault.dirOut}</span> },
    { key: "cp", header: t.vault.colCounterparty, cell: (e) => <Hash value={e.counterparty} /> },
    { key: "amount", header: t.vault.colAmount, align: "right", cell: (e) => <span className="amount" title={exactUnits(e.amount, dec(e.asset))}>{formatUnits(e.amount, dec(e.asset))} <span className="muted">{tokenOf(e.asset)}</span></span> },
    { key: "tx", header: t.vault.colTx, cell: (e) => <Hash value={e.tx_hash} href={e.explorer_url} />, mobile: "hide" },
    {
      key: "status", header: t.vault.colStatus, cell: (e) => (
        <span className={`pill no-dot ${e.status === "flagged" ? "pill-red" : e.status === "pending" ? "pill-amber" : "pill-green"}`}>{status(e.status)}</span>
      ),
    },
  ];
  return (
    <section className="section-block" aria-labelledby="mv-h">
      <div className="section-head">
        <h2 id="mv-h">{t.vault.movements}</h2>
        <span className="muted small">{t.vault.movementsHint}</span>
      </div>
      <DataTable caption={t.vault.movements} columns={cols} rows={pg.rows} rowKey={(e) => e.id}
        empty={<EmptyState title={t.vault.noMovements} />}
        footer={known.length > 0 && <Pager page={pg.page} limit={pg.limit} count={pg.rows.length} total={pg.total} onPage={pg.setPage} onLimit={pg.setLimit} />} />
    </section>
  );
}
